declare var google: any;
declare var FB: any;

import {
  Component,
  AfterViewInit,
  ElementRef,
  HostListener,
  ViewChild
} from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginService } from '../auth/login.service';
import Swal from 'sweetalert2';
import { DeviceDetectorService } from '../../../services/device-detector.service';
import { i18Service } from '../../../services/i18service';
import { finalize, from, switchMap } from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit {

  @ViewChild('heroCanvas', { static: false })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  person!: FormGroup;
  contentReady: boolean = false;
  loggedIn: boolean = false;
  userEmail!: string;
  userName!: string;
  userInfo: any;
  isMobile: boolean = false;
  isAr: boolean = false;
  userType: any;
  password: string = '';
  isPasswordVisible: boolean = false;
  languageDropdownOpen = false;
  languages = [
    { code: 'ar', label: 'Arabic', flag: '/assets/ar.png' },
    { code: 'en', label: 'English', flag: '/assets/en.png' },
    { code: 'fr', label: 'French', flag: '/assets/fr.png' },
  ];

  constructor(
    private _FormBuilder: FormBuilder,
    private _LoginService: LoginService,
    private _Router: Router,

    private deviceService: DeviceDetectorService,
    private i18: i18Service,
  ) { }

  @HostListener('document:click')
  closeLanguageDropdown(): void {
    this.languageDropdownOpen = false;
  }

  /* ===============================
     Angular Lifecycle
  =============================== */

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem("language") == "ar";


    if (localStorage.getItem("token") != null) {
      this.loggedIn = true;
      this.flowLog('existing token detected without redirect');
    }

    this.initiate();
  }

  ngAfterViewInit(): void {
    this.initCanvasAnimation();
  }

  /* ===============================
     Canvas Animation
  =============================== */

  initCanvasAnimation() {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d')!;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Coin {
      x!: number;
      y!: number;
      radius!: number;
      speed!: number;
      rotation!: number;

      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = -20;
        this.radius = 8 + Math.random() * 10;
        this.speed = 2 + Math.random() * 3;
        this.rotation = 0;
      }

      update() {
        this.y += this.speed;
        this.rotation += 0.05;

        if (this.y > canvas.height + 20) {
          this.reset();
        }
      }

      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.beginPath();
        ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = "#3b5aa8";
        ctx.fill();

        ctx.strokeStyle = "#3b5aa8";
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 14px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("B", 0, 1);

        ctx.restore();
      }
    }

    const coins: Coin[] = [];
    for (let i = 0; i < 30; i++) {
      coins.push(new Coin());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      coins.forEach(coin => {
        coin.update();
        coin.draw();
      });

      requestAnimationFrame(animate);
    };

    animate();
  }

  /* ===============================
     Login Logic
  =============================== */

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  get currentLanguage(): string {
    return localStorage.getItem('language') || 'en';
  }

  get currentLanguageOption() {
    return this.languages.find((language) => language.code === this.currentLanguage) ?? this.languages[1];
  }

  toggleLanguageDropdown(event?: Event): void {
    event?.stopPropagation();
    this.languageDropdownOpen = !this.languageDropdownOpen;
  }

  setLanguage(language: string, event?: Event): void {
    event?.stopPropagation();
    this.i18.setLanguage(language);
    this.isAr = language === 'ar';
    this.languageDropdownOpen = false;
  }

  initiate() {
    this.person = this._FormBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  private getRouteAfterLogin(): string {
    const route = String(this.userType) === "8"
      ? (this.isMobile ? '/orders.mobile' : '/Orders')
      : (this.isMobile ? '/Pospage.mobile' : '/Pospage');
    this.flowLog('selected route after login', {
      userType: this.userType,
      warehouseId: localStorage.getItem('posId'),
      route
    });
    return route;
  }

  onSubmit(): void {

    if (this.person.invalid) {
      this.person.markAllAsTouched();
      return;
    }

    this.contentReady = true;
    this.flowLog('submit start', { username: this.person.value?.username });

    this._LoginService.user_login(this.person.value)
      .subscribe({
        next: (res) => {
          this.flowLog('login api success', {
            responseUserType: res?.user?.type,
            responseWarehouseId: res?.user?.warehouse_id,
            tokenPrefix: res?.token?.slice(0, 8)
          });
          this.userType = res?.user?.type ?? res?.user_type ?? res?.type;

          // 1️⃣ خزّن البيانات الأول
          this._LoginService.initializeAuthenticatedSession(res).pipe(
            switchMap(() => from(this._Router.navigate([this.getRouteAfterLogin()]))),
            finalize(() => {
              this.contentReady = false;
              this.flowLog('login initialization finalize loader hidden');
            })
          ).subscribe({
            next: (navigated) => this.flowLog('router navigate result', { navigated }),
            error: (error) => {
              console.error("Session initialization error:", error);
              this.flowLog('session initialization error', { message: error?.message, status: error?.status });
              Swal.fire({
                icon: 'error',
                title: this.isAr ? "تعذر تجهيز الحساب" : "Could not initialize account",
                text: this.isAr ? "حاول تسجيل الدخول مرة أخرى" : "Please try logging in again",
              });
            }
          });

          // 2️⃣ اعرض الرسالة
          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? "اهلا بيك في بلوبوكس"
              : "Welcome to BlueBox",
            showConfirmButton: false,
            timer: 1200
          });



        },

        error: (err) => {

          console.error("Login Error:", err);
          this.contentReady = false;
          this.flowLog('login api error loader hidden', { message: err?.message, status: err?.status });
          Swal.fire({
            icon: 'error',
            title: this.isAr
              ? "البيانات اللي مدخلها فيها حاجة غلط"
              : "Wrong Username or Password",
            text: this.isAr
              ? "كلم الدعم"
              : "Please contact support",
          });

        },

        complete: () => {

        }
      });
  }

  private flowLog(step: string, extra: Record<string, any> = {}): void {}
}
